import { useState } from 'react';
import { Alert, Divider, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertCircle, IconPencil } from '@tabler/icons-react';
import { Button } from 'src/design-system/Button';
import { TermsAndPrivacy } from 'src/auth/components/TermsAndPrivacy';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { validateCompanyName } from 'src/common/utils/companyName';
import { WizardShell } from '../components/WizardShell';

type ReviewEditableField = 'firstName' | 'lastName';

type ReviewStepProps = {
  values: {
    firstName: string;
    lastName: string;
    companyName: string;
    frn: string;
    email: string;
  };
  direction: 1 | -1;
  currentStepIndex: number;
  totalSteps: number;
  onBack: () => void;
  onSubmit: () => void;
  onEditField: (field: ReviewEditableField) => void;
  onCompanyNameChange: (value: string) => void;
  isSubmitting: boolean;
  topLevelError: string | null;
  ambiguousTimeout: boolean;
};

const EDITABLE_ROWS: { field: ReviewEditableField; label: string }[] = [
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
];

export function ReviewStep({
  values,
  onBack,
  onSubmit,
  onEditField,
  onCompanyNameChange,
  isSubmitting,
  topLevelError,
  ambiguousTimeout,
}: ReviewStepProps) {
  return (
    <WizardShell
      title="Check your details"
      onBack={onBack}
      onAction={onSubmit}
      actionLabel={ambiguousTimeout ? 'Try again' : 'Create my account'}
      actionLoading={isSubmitting}
    >
      <Stack gap="lg">
        {topLevelError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="We could not complete your submission"
          >
            {topLevelError}
          </Alert>
        )}

        <Stack gap="xs">
          <ReviewRow label="Email address" value={values.email} />
          <Divider />
          {EDITABLE_ROWS.map(({ field, label }) => (
            <ReviewRow
              key={field}
              label={label}
              value={values[field]}
              onEdit={() => onEditField(field)}
            />
          ))}
          <CompanyNameRow value={values.companyName} onChange={onCompanyNameChange} />
          <ReviewRow label="FCA firm reference number" value={values.frn} />
        </Stack>

        <TermsAndPrivacy />

        {ambiguousTimeout && (
          <Text size="xs" c="gray.6" ta="center">
            Please wait a few minutes before trying again, in case your previous submission is still
            being processed.
          </Text>
        )}
      </Stack>
    </WizardShell>
  );
}

function ReviewRow({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit?: () => void;
}) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="xs" c="gray.6">
          {label}
        </Text>
        <Text fw={FONT_WEIGHTS.semibold} c="gray.10" truncate>
          {value}
        </Text>
      </Stack>
      {onEdit && (
        <Button
          variant="text"
          leftSection={<IconPencil size={14} />}
          onClick={onEdit}
          aria-label={`Edit ${label.toLowerCase().replace('fca', 'FCA')}`}
        >
          Edit
        </Button>
      )}
    </Group>
  );
}

// Unlike first/last name, editing this doesn't navigate back to an earlier step -
// there is no dedicated company-name step to return to, and the FRN this PR locks
// stays locked, so it's edited in place instead. Company name is cosmetic (it
// doesn't identify the firm - the FRN does), which is why it alone gets this
// exception; see the submit endpoint's `company_name` override.
function CompanyNameRow({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const error = validateCompanyName(draft);

  if (!isEditing) {
    return (
      <ReviewRow
        label="Company name"
        value={value}
        onEdit={() => {
          setDraft(value);
          setIsEditing(true);
        }}
      />
    );
  }

  return (
    <Stack gap="xs">
      <TextInput
        label="Company name"
        value={draft}
        onChange={event => setDraft(event.currentTarget.value)}
        error={error}
        data-autofocus
      />
      <Group justify="flex-end" gap="xs">
        <Button variant="text" onClick={() => setIsEditing(false)}>
          Cancel
        </Button>
        <Button
          variant="text"
          disabled={!!error}
          onClick={() => {
            onChange(draft.trim());
            setIsEditing(false);
          }}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );
}
